import React, { FC, useState } from "react";
import styled from "@emotion/styled";
import { Select, Dialog, Switch, Text } from "@radix-ui/themes";
import { Button } from "@/components/button";
import { TERMINAL_FONT_FAMILY } from "./shared/constants";

interface ProcessingParams {
  timeout_seconds?: number;
  immediate?: boolean;
}

interface TriggerProcessingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTrigger: (params: ProcessingParams) => Promise<void>;
  isProcessing: boolean;
}

const TIMEOUT_OPTIONS = [
  { value: "0", label: "No timeout (immediate)" },
  { value: "10", label: "10 seconds" },
  { value: "30", label: "30 seconds" },
  { value: "60", label: "1 minute" },
  { value: "300", label: "5 minutes" },
  { value: "600", label: "10 minutes" },
] as const;

export const TriggerProcessingModal: FC<TriggerProcessingModalProps> = ({
  isOpen,
  onClose,
  onTrigger,
  isProcessing,
}) => {
  const [timeoutSeconds, setTimeoutSeconds] = useState<string>("0");
  const [immediate, setImmediate] = useState<boolean>(true);

  const handleSubmit = async () => {
    const params: ProcessingParams = {
      timeout_seconds: parseInt(timeoutSeconds, 10),
      immediate,
    };

    await onTrigger(params);
    setTimeoutSeconds("0");
    setImmediate(true);
    onClose();
  };

  const handleClose = () => {
    setTimeoutSeconds("0");
    setImmediate(true);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Content style={{ maxWidth: "500px" }}>
        <DialogTitle>Process Session</DialogTitle>
        <DialogDescription size="2" mb="4">
          Configure processing parameters for the session workflow
        </DialogDescription>

        <FormContainer>
          <FormField>
            <Label>Processing Timeout</Label>
            <Select.Root value={timeoutSeconds} onValueChange={setTimeoutSeconds}>
              <Select.Trigger placeholder="Select timeout..." style={{ width: "100%" }} />
              <Select.Content>
                {TIMEOUT_OPTIONS.map(option => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
            <HelpText>How long to wait before starting processing.</HelpText>
          </FormField>

          <FormField>
            <SwitchContainer>
              <Switch checked={immediate} onCheckedChange={setImmediate} size="1" />
              <SwitchLabel>Process immediately</SwitchLabel>
            </SwitchContainer>
            <HelpText>
              If enabled, processing starts immediately regardless of timeout setting.
            </HelpText>
          </FormField>

          <ActionButtons>
            <Button onClick={handleClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isProcessing}>
              {isProcessing ? "Starting..." : "Start Processing"}
            </Button>
          </ActionButtons>
        </FormContainer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

const DialogTitle = styled(Dialog.Title)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
`;

const DialogDescription = styled(Dialog.Description)`
  font-family: ${TERMINAL_FONT_FAMILY};
  font-size: 13px;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-12);
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const HelpText = styled(Text)`
  font-size: 11px;
  color: var(--gray-10);
  font-family: ${TERMINAL_FONT_FAMILY};
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SwitchLabel = styled.label`
  font-size: 13px;
  color: var(--gray-12);
  font-family: ${TERMINAL_FONT_FAMILY};
  cursor: pointer;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
